import _ from 'lodash'
import { createSelector } from 'reselect'
import {
    RegistrationForm,
    GetRegistrationFormRequest,
    CreateRegistrationFormRequest,
    UpdateRegistrationFormRequest,
    RegistrationResult,
    GetRegistrationResultsRequest,
    RequestExportRegistrationResultsPayload,
    MassApproveByUsernameRequest,
    ReorderWaitlistPayload,
} from 'services/api/registrations'
import { BaseAction, State } from 'state'
import { selectSelectedCampaignSummary } from './review'
import { MassError } from './submissions'
import { MASS_REGISTRATION_ACTION_NAME_ENUM } from './review'
import { TimeWindowState } from './banners'

export enum BLOCK_TYPE {
    TEXT = 'text_block',
    PHOTOS = 'photos_block',
    QUESTION = 'question_block',
    PHOTO_SUBMISSION = 'photo_submission_block',
    URL_SUBMISSION = 'url_submission_block',
    NUMBER_SUBMISSION = 'number_submission_block',
    TEXT_SUBMISSION = 'text_submission_block',
}

export interface MassApproveResult {
    username: string
    message?: string
}

interface MassApproveProgress {
    count: number
    total: number
}
interface MassApprove {
    success: MassApproveResult[]
    failed: MassApproveResult[]
    progress: MassApproveProgress
}

export interface RegistrationResultState extends Omit<RegistrationResult, 'partiposter_profile'> {
    partiposterId: string
}

export interface RegistrationsState {
    form: RegistrationForm | null
    registrations: string[]
    byId: Record<string, RegistrationResultState>
    priorityList: string[]
    priorityListById: Record<string, RegistrationResultState>
    massApprove: MassApprove
    viewedRows: string[]
    highlightedRegistrations: string[]
}

export interface MassRegistrationPayload {
    action_name: MASS_REGISTRATION_ACTION_NAME_ENUM
    campaignUuid: string
    uuids: string[]
    dataType?: 'registration' | 'priorityList'
    getRegistrationListPayload?: Omit<GetRegistrationResultsRequest, 'campaignUuid'>
}

export interface MassRegistrationResult extends MassRegistrationPayload {
    success_count?: number
    errors?: MassError[]
    data?: any
}

export interface PriorityListOrderingPayload {
    campaignUuid: string
    sortingPriorityList: string[]
}

export interface PriorityListOrderingResult {
    success_count?: number
    errors?: MassError[]
}

export interface UpdateRegistrationScheduleRequestPayload {
    uuid: string
    time_window: TimeWindowState
    approval_expiration_days: number
}

export enum APPROVE_ORDER_MODE {
    REGISTRARION_TIME = 'registration_time',
    WAITLISTED_TIME = 'waitlisted_time',
}

export interface WaitlistOrderModePayload {
    waitlist_order_mode: APPROVE_ORDER_MODE
}

export interface UpdateWaitlistOrderModePayload extends WaitlistOrderModePayload {
    campaignUuid: string
}

export interface UpdateViewedRowsPayload {
    rowId: string
}

export interface HighlightRegistrationPayload {
    campaignUuid: string
    registrationUuid: string
    highlighted: boolean
}

export type ScheduleType = {
    start: string | null
    end: string | null
}

const initialState: RegistrationsState = {
    form: null,
    registrations: [],
    byId: {},
    priorityList: [],
    priorityListById: {},
    viewedRows: [],
    highlightedRegistrations: [],
    massApprove: {
        success: [],
        failed: [],
        progress: {
            count: 0,
            total: 0,
        },
    },
}

export enum RegistrationsActions {
    GET_FORM_REQUEST = 'registrations/GET_FORM_REQUEST',
    CREATE_FORM_REQUEST = 'registrations/CREATE_FORM_REQUEST',
    UPDATE_FORM_REQUEST = 'registrations/UPDATE_FORM_REQUEST',
    SAVE_FORM = 'registrations/SAVE_FORM',
    SAVE_REGISTRATION_RESULTS_LIST = 'registration/SAVE_REGISTRATION_RESULTS_LIST',
    CLEAR_REGISTRATION_RESULTS_LIST = 'registrations/CLEAR_REGISTRATION_RESULTS_LIST',
    REGISTRATION_RESULTS_LIST_REQUEST = 'registrations/REGISTRATION_RESULTS_LIST_REQUEST',
    REGISTRATION_PRIORITY_LIST_REQUEST = 'registrations/REGISTRATION_PRIORITY_LIST_REQUEST',
    EXPORT_REGISTRATION_RESULTS_LIST_REQUEST = 'registrations/EXPORT_REGISTRATION_RESULTS_LIST_REQUEST',
    MASS_REGISTRATION = 'registrations/MASS_REGISTRATION',
    UPDATE_MASS_REGISTRATION_RESULT = 'registrations/UPDATE_MASS_REGISTRATION_RESULT',
    MASS_APPROVE_BY_USERNAME = 'registrations/MASS_APPROVE_BY_USERNAME',
    SAVE_MASS_APPROVE_PROGRESS = 'registrations/SAVE_MASS_APPROVE_PROGRESS',
    SAVE_MASS_APPROVE_RESULT = 'registrations/SAVE_MASS_APPROVE_RESULT',
    SAVE_PRIORITY_LIST = 'registrations/SAVE_PRIORITY_LIST',
    CLEAR_PRIORITY_LIST = 'registrations/CLEAR_PRIORITY_LIST',
    SAVE_PRIORITY_LIST_ORDERING = 'registrations/SAVE_PRIORITY_LIST_ORDERING',
    UPDATE_PRIORITY_LIST_ORDERING = 'registrations/UPDATE_PRIORITY_LIST_ORDERING',
    UPDATE_MASS_WAITLIST_TO_PRIORITY_LIST_RESULT = 'registrations/UPDATE_MASS_WAITLIST_TO_PRIORITY_LIST_RESULT',
    UPDATE_MASS_PRIORITY_LIST_TO_WAITLIST_RESULT = 'registrations/UPDATE_MASS_PRIORITY_LIST_TO_WAITLIST_RESULT',
    UPDATE_MASS_PRIORITY_LIST_RESULT = 'registrations/UPDATE_MASS_PRIORITY_LIST_RESULT',
    UPDATE_WAITLIST_ORDER_MODE = 'registrations/UPDATE_WAITLIST_ORDER_MODE',
    SAVE_WAITLIST_ORDER_MODE = 'registrations/SAVE_WAITLIST_ORDER_MODE',
    UPDATE_SCHEDULE_REQUEST = 'registrations/UPDATE_SCHEDULE_REQUEST',
    UPDATE_VIEWED_ROWS = 'registrations/UPDATE_VIEWED_ROWS',
    SYNCED_VIEWED_ROWS = 'registrations/SYNCED_VIEWED_ROWS',
    SET_REGISTRATION_STEP_DATA = 'registrations/SET_REGISTRATION_STEP_DATA',
    HIGHLIGHT_REGISTRATION = 'registrations/HIGHLIGHT_REGISTRATION',
    REORDER_WAITLIST = 'registrations/REORDER_WAITLIST',
}

export const massApproveByUsername = (payload: MassApproveByUsernameRequest) => ({
    payload,
    type: RegistrationsActions.MASS_APPROVE_BY_USERNAME,
})

export const saveMassApproveProgress = (payload: MassApproveProgress) => ({
    payload,
    type: RegistrationsActions.SAVE_MASS_APPROVE_PROGRESS,
})

export const saveMassApproveResult = (payload: { success: MassApproveResult[]; failed: MassApproveResult[] }) => ({
    payload,
    type: RegistrationsActions.SAVE_MASS_APPROVE_RESULT,
})

export const getForm = (payload: GetRegistrationFormRequest) => ({
    payload,
    type: RegistrationsActions.GET_FORM_REQUEST,
})

export const createForm = (payload: CreateRegistrationFormRequest) => ({
    payload,
    type: RegistrationsActions.CREATE_FORM_REQUEST,
})

export const updateForm = (payload: UpdateRegistrationFormRequest) => ({
    payload,
    type: RegistrationsActions.UPDATE_FORM_REQUEST,
})

export const saveForm = (payload: RegistrationForm | null) => ({
    payload,
    type: RegistrationsActions.SAVE_FORM,
})

export const getRegistrationsList = (payload: GetRegistrationResultsRequest) => ({
    payload,
    type: RegistrationsActions.REGISTRATION_RESULTS_LIST_REQUEST,
})

export const saveRegistrationsList = (payload: RegistrationResultState[]) => ({
    payload,
    type: RegistrationsActions.SAVE_REGISTRATION_RESULTS_LIST,
})

export const clearRegistrationsList = () => ({
    type: RegistrationsActions.CLEAR_REGISTRATION_RESULTS_LIST,
})

export const exportRegistrationsList = (payload: RequestExportRegistrationResultsPayload) => ({
    payload,
    type: RegistrationsActions.EXPORT_REGISTRATION_RESULTS_LIST_REQUEST,
})

export const getPriorityList = (payload: GetRegistrationResultsRequest) => ({
    payload,
    type: RegistrationsActions.REGISTRATION_PRIORITY_LIST_REQUEST,
})

export const savePriorityList = (payload: RegistrationResultState[]) => ({
    payload,
    type: RegistrationsActions.SAVE_PRIORITY_LIST,
})

export const clearPriorityList = () => ({
    type: RegistrationsActions.CLEAR_PRIORITY_LIST,
})

export const updateMassWaitlistToPriorityListResult = (payload: MassRegistrationResult) => ({
    payload,
    type: RegistrationsActions.UPDATE_MASS_WAITLIST_TO_PRIORITY_LIST_RESULT,
})

export const updateMassPriorityListToWaitlistResult = (payload: MassRegistrationResult) => ({
    payload,
    type: RegistrationsActions.UPDATE_MASS_PRIORITY_LIST_TO_WAITLIST_RESULT,
})

export const updateMassPriorityListResult = (payload: MassRegistrationResult) => ({
    payload,
    type: RegistrationsActions.UPDATE_MASS_PRIORITY_LIST_RESULT,
})

export const updateMassRegistrationResult = (payload: MassRegistrationResult) => ({
    payload,
    type: RegistrationsActions.UPDATE_MASS_REGISTRATION_RESULT,
})

export const massRegistration = (payload: MassRegistrationPayload) => ({
    payload,
    type: RegistrationsActions.MASS_REGISTRATION,
})

export const savePriorityListOrdering = (payload: PriorityListOrderingPayload) => ({
    payload,
    type: RegistrationsActions.SAVE_PRIORITY_LIST_ORDERING,
})

export const updatePriorityListOrdering = (payload: PriorityListOrderingPayload) => ({
    payload,
    type: RegistrationsActions.UPDATE_PRIORITY_LIST_ORDERING,
})

export const updateWaitlistOrderMode = (payload: UpdateWaitlistOrderModePayload) => ({
    payload,
    type: RegistrationsActions.UPDATE_WAITLIST_ORDER_MODE,
})

export const saveWaitlistOrderMode = (payload: WaitlistOrderModePayload) => ({
    payload,
    type: RegistrationsActions.SAVE_WAITLIST_ORDER_MODE,
})

export const updateRegistrationSchedule = (payload: UpdateRegistrationScheduleRequestPayload) => ({
    payload,
    type: RegistrationsActions.UPDATE_SCHEDULE_REQUEST,
})

export const updateViewedRows = (payload: UpdateViewedRowsPayload) => ({
    payload,
    type: RegistrationsActions.UPDATE_VIEWED_ROWS,
})

export const syncViewedRows = (payload: { campaignUuid: string; registrationId: string }) => ({
    payload,
    type: RegistrationsActions.SYNCED_VIEWED_ROWS,
})

export const highlightRegistration = (payload: HighlightRegistrationPayload) => ({
    payload,
    type: RegistrationsActions.HIGHLIGHT_REGISTRATION,
})

export const reorderWaitlist = (payload: ReorderWaitlistPayload) => ({
    payload,
    type: RegistrationsActions.REORDER_WAITLIST,
})

export const registrationsReducer = (state: RegistrationsState = initialState, action: BaseAction) => {
    switch (action.type) {
        case RegistrationsActions.SAVE_FORM:
            return { ...state, form: action.payload }
        case RegistrationsActions.SAVE_WAITLIST_ORDER_MODE:
            return { ...state, form: { ...state.form, ...action.payload } }
        case RegistrationsActions.SAVE_REGISTRATION_RESULTS_LIST:
            const data = action.payload
            const uuids = data.map((registration: RegistrationResultState) => registration.uuid)
            const viewedRegistration = data.filter(
                (registration: RegistrationResultState) => registration.viewed_at && registration.viewed_at !== null,
            )
            const viewedUuids = viewedRegistration.map((registration: RegistrationResultState) => registration.uuid)
            const highlightedRegistration = data.filter(
                (registration: RegistrationResultState) => registration.highlighted,
            )
            const highlightedUuids = highlightedRegistration.map(
                (registration: RegistrationResultState) => registration.uuid,
            )
            const byId = _.keyBy(data, 'uuid')

            return {
                ...state,
                registrations: _.uniq([...state.registrations, ...uuids]),
                byId: {
                    ...state.byId,
                    ...byId,
                },
                viewedRows: viewedUuids,
                highlightedRegistrations: highlightedUuids,
            }
        case RegistrationsActions.CLEAR_REGISTRATION_RESULTS_LIST:
            return Object.assign({}, state, {
                registrations: [],
                byId: {},
            })
        case RegistrationsActions.SAVE_PRIORITY_LIST:
            const priorityList = action.payload
            const listUuids = priorityList.map((registration: RegistrationResultState) => registration.uuid)
            const priorityListById = _.keyBy(priorityList, 'uuid')
            return {
                ...state,
                priorityList: _.uniq([...state.priorityList, ...listUuids]),
                priorityListById: {
                    ...state.priorityListById,
                    ...priorityListById,
                },
            }
        case RegistrationsActions.UPDATE_PRIORITY_LIST_ORDERING:
            const { sortingPriorityList } = action.payload
            return {
                ...state,
                priorityList: state.priorityList.sort(
                    (a, b) => sortingPriorityList.indexOf(a) - sortingPriorityList.indexOf(b),
                ),
            }
        case RegistrationsActions.CLEAR_PRIORITY_LIST:
            return Object.assign({}, state, {
                priorityList: [],
                priorityListById: {},
            })
        case RegistrationsActions.UPDATE_MASS_WAITLIST_TO_PRIORITY_LIST_RESULT: {
            const { data } = action.payload
            const successRegistrationIds = action.payload.uuids.filter((uuid: string) => {
                if (data.errors.length > 0) {
                    return !data.errors.some((error: any) => error.uuid == uuid)
                }
                return true
            })

            const updatedState = Object.assign({}, state, {
                ...state,
                byId: data.errors.reduce(
                    (memo: Record<string, RegistrationResult>, error: MassError) =>
                        Object.assign({}, memo, { [error.uuid]: { ...state.byId[error.uuid], error: error.error } }),
                    state.byId,
                ),
            })

            const updatePriorityListById = successRegistrationIds.reduce(
                (memo: Record<string, RegistrationResult>, uuid: string) => {
                    return { ...memo, [uuid]: updatedState.byId[uuid] }
                },
                {},
            )

            return Object.assign({}, updatedState, {
                ...updatedState,
                registrations: updatedState.registrations.filter(
                    (uuid: string) => !successRegistrationIds.includes(uuid),
                ),
                byId: Object.keys(updatedState.byId)
                    .filter(uuid => !successRegistrationIds.includes(uuid))
                    .reduce((memo: Record<string, RegistrationResult>, uuid: string) => {
                        return { ...memo, [uuid]: updatedState.byId[uuid] }
                    }, {}),
                priorityList: _.uniq([...state.priorityList, ...successRegistrationIds]),
                priorityListById: {
                    ...state.priorityListById,
                    ...updatePriorityListById,
                },
            })
        }
        case RegistrationsActions.UPDATE_MASS_PRIORITY_LIST_TO_WAITLIST_RESULT: {
            const { data } = action.payload
            const successPriorityListIds = action.payload.uuids.filter((uuid: string) => {
                if (data.errors.length > 0) {
                    return !data.errors.some((error: any) => error.uuid == uuid)
                }
                return true
            })

            const updatedState = Object.assign({}, state, {
                ...state,
                priorityListById: data.errors.reduce(
                    (memo: Record<string, RegistrationResult>, error: MassError) =>
                        Object.assign({}, memo, {
                            [error.uuid]: { ...state.priorityListById[error.uuid], error: error.error },
                        }),
                    state.priorityListById,
                ),
            })

            const updateRegistrationById = successPriorityListIds.reduce(
                (memo: Record<string, RegistrationResult>, uuid: string) => {
                    return { ...memo, [uuid]: updatedState.priorityListById[uuid] }
                },
                {},
            )

            return Object.assign({}, updatedState, {
                ...updatedState,
                priorityList: updatedState.priorityList.filter(
                    (uuid: string) => !successPriorityListIds.includes(uuid),
                ),
                priorityListById: Object.keys(updatedState.priorityListById)
                    .filter(uuid => !successPriorityListIds.includes(uuid))
                    .reduce((memo: Record<string, RegistrationResult>, uuid: string) => {
                        return { ...memo, [uuid]: updatedState.priorityListById[uuid] }
                    }, {}),
                registrations: _.uniq([...state.registrations, ...successPriorityListIds]),
                byId: {
                    ...state.byId,
                    ...updateRegistrationById,
                },
            })
        }
        case RegistrationsActions.UPDATE_MASS_PRIORITY_LIST_RESULT: {
            const { data } = action.payload
            const successRegistrationIds = action.payload.uuids.filter((uuid: string) => {
                if (data.errors.length > 0) {
                    return !data.errors.some((error: any) => error.uuid == uuid)
                }
                return true
            })
            const updatedState = Object.assign({}, state, {
                ...state,
                priorityListById: data.errors.reduce(
                    (memo: Record<string, RegistrationResult>, error: MassError) =>
                        Object.assign({}, memo, {
                            [error.uuid]: { ...state.priorityListById[error.uuid], error: error.error },
                        }),
                    state.priorityListById,
                ),
            })

            return Object.assign({}, updatedState, {
                ...updatedState,
                priorityList: updatedState.priorityList.filter(
                    (uuid: string) => !successRegistrationIds.includes(uuid),
                ),
                priorityListById: Object.keys(updatedState.priorityListById)
                    .filter(uuid => !successRegistrationIds.includes(uuid))
                    .reduce((memo: Record<string, RegistrationResult>, uuid: string) => {
                        return { ...memo, [uuid]: updatedState.priorityListById[uuid] }
                    }, {}),
            })
        }
        case RegistrationsActions.UPDATE_MASS_REGISTRATION_RESULT: {
            const { data } = action.payload
            const successRegistrationIds = action.payload.uuids.filter((uuid: string) => {
                if (data.errors.length > 0) {
                    return !data.errors.some((error: any) => error.uuid == uuid)
                }
                return true
            })
            const updatedState = Object.assign({}, state, {
                ...state,
                byId: data.errors.reduce(
                    (memo: Record<string, RegistrationResult>, error: MassError) =>
                        Object.assign({}, memo, { [error.uuid]: { ...state.byId[error.uuid], error: error.error } }),
                    state.byId,
                ),
            })

            return Object.assign({}, updatedState, {
                ...updatedState,
                registrations: updatedState.registrations.filter(
                    (uuid: string) => !successRegistrationIds.includes(uuid),
                ),
                byId: Object.keys(updatedState.byId)
                    .filter(uuid => !successRegistrationIds.includes(uuid))
                    .reduce((memo: Record<string, RegistrationResult>, uuid: string) => {
                        return { ...memo, [uuid]: updatedState.byId[uuid] }
                    }, {}),
            })
        }
        case RegistrationsActions.SAVE_MASS_APPROVE_PROGRESS:
            return {
                ...state,
                massApprove: {
                    ...state.massApprove,
                    progress: action.payload,
                },
            }
        case RegistrationsActions.SAVE_MASS_APPROVE_RESULT:
            return {
                ...state,
                massApprove: {
                    ...state.massApprove,
                    ...action.payload,
                },
            }
        case RegistrationsActions.UPDATE_VIEWED_ROWS:
            const viewedRowId = action.payload.rowId
            const viewedRows = _.cloneDeep(state.viewedRows)
            if (state.viewedRows.includes(viewedRowId)) {
                return state
            } else {
                viewedRows.push(viewedRowId)
                return Object.assign({}, state, { ...state, viewedRows: viewedRows })
            }
        case RegistrationsActions.HIGHLIGHT_REGISTRATION:
            const { registrationUuid } = action.payload
            const highlightedRegistrations = _.cloneDeep(state.highlightedRegistrations)
            if (state.highlightedRegistrations.includes(registrationUuid)) {
                const filteredRegistrations = highlightedRegistrations.filter(item => item !== registrationUuid)
                return {
                    ...state,
                    highlightedRegistrations: filteredRegistrations,
                }
            } else {
                highlightedRegistrations.push(registrationUuid)
                return {
                    ...state,
                    highlightedRegistrations,
                }
            }

        default:
            return state
    }
}

export const selectRegistrationForm = (state: State) => state.registrations.form

export const selectList = (state: RegistrationsState) => state.registrations.map((uuid: string) => state.byId[uuid])

export const selectPriorityList = (state: RegistrationsState) =>
    state.priorityList.map((uuid: string) => state.priorityListById[uuid])

export const selectRegistrationMassApproveProgress = (state: State): MassApproveProgress =>
    state.registrations.massApprove.progress

export const selectRegistrationMassApproveSuccess = (state: State) =>
    state.registrations.massApprove.success.map((item, index) => ({
        id: index,
        username: item.username,
    }))

export const selectRegistrationMassApproveFailed = (state: State) =>
    state.registrations.massApprove.failed.map((item, index) => ({
        id: index,
        username: item.username,
        message: item.message,
    }))

export const selectRegistrationRemainingSlots = createSelector(
    [selectRegistrationForm, state => selectSelectedCampaignSummary(state)],
    (registrationForm, campaignSummary) => {
        if (!registrationForm || !campaignSummary || !campaignSummary.registration_result_summary) {
            return undefined
        }
        return registrationForm.max_slots - campaignSummary.registration_result_summary.used_slots_count
    },
)

export const selectRegistrationUsedSlots = (state: State): number | undefined => {
    const campaignSummary = selectSelectedCampaignSummary(state)
    if (!campaignSummary || !campaignSummary.registration_result_summary) {
        return undefined
    }
    return campaignSummary.registration_result_summary.used_slots_count
}

export const selectRegistrationMaxSlots = (state: State): number | undefined => {
    const registrationForm = selectRegistrationForm(state)
    if (!registrationForm) {
        return undefined
    }
    return registrationForm.max_slots
}

export const selectIsWaitlistEnabled = createSelector(selectRegistrationForm, registrationForm =>
    Boolean(registrationForm && registrationForm.max_slots !== null && registrationForm.max_slots > 0),
)

export const selectWaitlistOrderMode = (state: State): APPROVE_ORDER_MODE | null =>
    state.registrations.form ? state.registrations.form.waitlist_order_mode : null

export const selectViewedRows = (state: State): string[] => {
    return state.registrations.viewedRows
}

export const selectHighlightedRegistrations = (state: State): string[] => {
    return state.registrations.highlightedRegistrations
}

export const selectRegistrationDate = (state: State): ScheduleType => {
    return state.registrations.form?.time_window ? state.registrations.form?.time_window : { start: null, end: null }
}
